import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'caigen.focus:v2';
const LEGACY_STORAGE_KEY = 'caigen.pomodoro:v1';
const MAX_SESSIONS = 80;

export interface FocusSession {
  id: string;
  task: string;
  startedAt: number;
  endedAt: number;
  seconds: number;
  completed: boolean;
}

interface StoredFocusState {
  version: 2;
  mode: 'focus' | 'break';
  focusMinutes: number;
  breakMinutes: number;
  remaining: number;
  target: number;
  startedAt: number;
  task: string;
  autoBreak: boolean;
  distractionFree: boolean;
  sessions: FocusSession[];
}

interface FocusContextValue extends StoredFocusState {
  running: boolean;
  durationSeconds: number;
  progress: number;
  todayMinutes: number;
  todaySessions: number;
  weekMinutes: number[];
  setTask: (task: string) => void;
  setFocusMinutes: (minutes: number) => void;
  setAutoBreak: (value: boolean) => void;
  setDistractionFree: (value: boolean) => void;
  start: () => void;
  pause: () => void;
  reset: () => void;
  finish: () => void;
  returnToFocus: () => void;
}

const DEFAULT_STATE: StoredFocusState = {
  version: 2,
  mode: 'focus',
  focusMinutes: 25,
  breakMinutes: 5,
  remaining: 25 * 60,
  target: 0,
  startedAt: 0,
  task: '',
  autoBreak: true,
  distractionFree: true,
  sessions: [],
};

const FocusContext = createContext<FocusContextValue | null>(null);

function clampMinutes(value: number) {
  return Math.min(180, Math.max(5, Math.round(value)));
}

function dateKey(value: number) {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function readState(): StoredFocusState {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as Partial<StoredFocusState> | null;
    if (parsed?.version === 2) {
      const focusMinutes = clampMinutes(Number(parsed.focusMinutes) || 25);
      const breakMinutes = clampMinutes(Number(parsed.breakMinutes) || 5);
      const mode = parsed.mode === 'break' ? 'break' : 'focus';
      const duration = (mode === 'break' ? breakMinutes : focusMinutes) * 60;
      return {
        ...DEFAULT_STATE,
        ...parsed,
        version: 2,
        mode,
        focusMinutes,
        breakMinutes,
        remaining: Math.min(duration, Math.max(0, Number(parsed.remaining) || duration)),
        target: Math.max(0, Number(parsed.target) || 0),
        startedAt: Math.max(0, Number(parsed.startedAt) || 0),
        task: typeof parsed.task === 'string' ? parsed.task.slice(0, 120) : '',
        sessions: Array.isArray(parsed.sessions) ? parsed.sessions.slice(0, MAX_SESSIONS) : [],
      };
    }
    const legacy = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || 'null') as { remaining?: number; target?: number } | null;
    if (legacy) return { ...DEFAULT_STATE, remaining: Math.max(0, Number(legacy.remaining) || DEFAULT_STATE.remaining), target: Math.max(0, Number(legacy.target) || 0) };
  } catch { /* local focus state is optional */ }
  return DEFAULT_STATE;
}

function writeState(value: StoredFocusState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* timer still works without storage */ }
}

function startOfWeek(now: number) {
  const date = new Date(now);
  const weekday = date.getDay() || 7;
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - weekday + 1);
  return date.getTime();
}

export function FocusProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<StoredFocusState>(readState);
  const [now, setNow] = useState(Date.now());
  const durationSeconds = (stored.mode === 'break' ? stored.breakMinutes : stored.focusMinutes) * 60;
  const remaining = stored.target ? Math.max(0, Math.ceil((stored.target - now) / 1000)) : stored.remaining;
  const running = stored.target > 0;

  const commit = (update: (current: StoredFocusState) => StoredFocusState) => {
    setStored((current) => {
      const next = update(current);
      writeState(next);
      return next;
    });
    setNow(Date.now());
  };

  useEffect(() => {
    if (!stored.target) return;
    const tick = () => setNow(Date.now());
    tick();
    const timer = window.setInterval(tick, 500);
    return () => window.clearInterval(timer);
  }, [stored.target]);

  useEffect(() => {
    if (!stored.target || remaining > 0) return;
    commit((current) => {
      if (!current.target) return current;
      const endedAt = Date.now();
      if (current.mode === 'break') return { ...current, mode: 'focus', remaining: current.focusMinutes * 60, target: 0, startedAt: 0 };
      const session: FocusSession = {
        id: `${endedAt}-${Math.random().toString(36).slice(2, 7)}`,
        task: current.task.trim() || '未命名专注',
        startedAt: current.startedAt || endedAt - current.focusMinutes * 60000,
        endedAt,
        seconds: current.focusMinutes * 60,
        completed: true,
      };
      const nextTarget = current.autoBreak ? endedAt + current.breakMinutes * 60000 : 0;
      return { ...current, mode: 'break', remaining: current.breakMinutes * 60, target: nextTarget, startedAt: nextTarget ? endedAt : 0, sessions: [session, ...current.sessions].slice(0, MAX_SESSIONS) };
    });
  }, [remaining, stored.target]);

  const todayKey = dateKey(now);
  const todays = stored.sessions.filter((session) => dateKey(session.endedAt) === todayKey);
  const weekStart = startOfWeek(now);
  const weekMinutes = Array.from({ length: 7 }, (_, index) => {
    const from = weekStart + index * 86400000;
    const to = from + 86400000;
    return Math.round(stored.sessions.filter((session) => session.endedAt >= from && session.endedAt < to).reduce((sum, session) => sum + session.seconds, 0) / 60);
  });

  const value = useMemo<FocusContextValue>(() => ({
    ...stored,
    remaining,
    running,
    durationSeconds,
    progress: Math.min(1, Math.max(0, (durationSeconds - remaining) / durationSeconds)),
    todayMinutes: Math.round(todays.reduce((sum, session) => sum + session.seconds, 0) / 60),
    todaySessions: todays.length,
    weekMinutes,
    setTask(task) { commit((current) => ({ ...current, task: task.slice(0, 120) })); },
    setFocusMinutes(minutes) {
      const next = clampMinutes(minutes);
      commit((current) => current.target || current.mode === 'break' ? current : { ...current, focusMinutes: next, remaining: next * 60, startedAt: 0 });
    },
    setAutoBreak(autoBreak) { commit((current) => ({ ...current, autoBreak })); },
    setDistractionFree(distractionFree) { commit((current) => ({ ...current, distractionFree })); },
    start() {
      commit((current) => {
        if (current.target) return current;
        const seconds = current.remaining > 0 ? current.remaining : (current.mode === 'break' ? current.breakMinutes : current.focusMinutes) * 60;
        const startedAt = current.startedAt || Date.now();
        return { ...current, remaining: seconds, target: Date.now() + seconds * 1000, startedAt };
      });
    },
    pause() {
      commit((current) => current.target ? { ...current, remaining: Math.max(0, Math.ceil((current.target - Date.now()) / 1000)), target: 0 } : current);
    },
    reset() {
      commit((current) => ({ ...current, remaining: (current.mode === 'break' ? current.breakMinutes : current.focusMinutes) * 60, target: 0, startedAt: 0 }));
    },
    finish() {
      commit((current) => {
        const total = (current.mode === 'break' ? current.breakMinutes : current.focusMinutes) * 60;
        const left = current.target ? Math.max(0, Math.ceil((current.target - Date.now()) / 1000)) : current.remaining;
        const elapsed = Math.max(0, total - left);
        const sessions = current.mode === 'focus' && elapsed >= 60 ? [{ id: `${Date.now()}-partial`, task: current.task.trim() || '未命名专注', startedAt: current.startedAt || Date.now() - elapsed * 1000, endedAt: Date.now(), seconds: elapsed, completed: false }, ...current.sessions].slice(0, MAX_SESSIONS) : current.sessions;
        return { ...current, mode: 'focus', remaining: current.focusMinutes * 60, target: 0, startedAt: 0, sessions };
      });
    },
    returnToFocus() { commit((current) => ({ ...current, mode: 'focus', remaining: current.focusMinutes * 60, target: 0, startedAt: 0 })); },
  // `now` intentionally invalidates the derived timer once per tick.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [stored, remaining, running, durationSeconds, todayKey, now]);

  return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>;
}

export function useFocusTimer() {
  const context = useContext(FocusContext);
  if (!context) throw new Error('useFocusTimer must be used inside FocusProvider');
  return context;
}

export function formatFocusTime(seconds: number) {
  const minutes = Math.floor(Math.max(0, seconds) / 60).toString().padStart(2, '0');
  const rest = Math.max(0, seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${rest}`;
}
